import { useEffect, useState } from "react";
import { Box, Button, Divider, List, Popover, Skeleton, Stack, Typography } from "@mui/material";

import { AppNotification } from "@interfaces/notification";
import { useLazyGetNotificationsQuery, useMarkAllReadMutation } from "@services/api/notifications";

import { NotificationItem } from "./NotificationItem";
import { NotificationsEmpty } from "./NotificationsEmpty";

const PAGE_LIMIT = 20;

export interface NotificationsPanelProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
}

export const NotificationsPanel = ({ anchorEl, onClose }: NotificationsPanelProps) => {
    const open = Boolean(anchorEl);

    const [items, setItems] = useState<Array<AppNotification>>([]);
    const [cursor, setCursor] = useState<number | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [isError, setIsError] = useState(false);

    const [triggerGetNotifications, { isFetching }] = useLazyGetNotificationsQuery();
    const [markAllRead, { isLoading: isMarkingAllRead }] = useMarkAllReadMutation();

    const isFirstLoad = isFetching && items.length === 0;
    const unreadCount = items.filter((n) => !n.read_at).length;

    const loadPage = async (afterCursor: number | null) => {
        setIsError(false);
        try {
            const params = new URLSearchParams({ limit: String(PAGE_LIMIT) });
            if (afterCursor !== null) params.set("cursor", String(afterCursor));

            const page = await triggerGetNotifications(params.toString()).unwrap();

            setItems((prev) => (afterCursor === null ? page.items : [...prev, ...page.items]));
            setCursor(page.next_cursor);
            setHasMore(page.has_more);
        } catch {
            setIsError(true);
        }
    };

    useEffect(() => {
        if (!open) return;

        setItems([]);
        setCursor(null);
        setHasMore(false);
        loadPage(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { width: { xs: "100vw", sm: 380 }, maxHeight: 480 } } }}
        >
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Notificaciones
                </Typography>
                <Button
                    size="small"
                    disabled={unreadCount === 0 || isMarkingAllRead}
                    onClick={() => {
                        markAllRead();
                        setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })));
                    }}
                >
                    Marcar todas como leídas
                </Button>
            </Stack>
            <Divider />

            <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
                {isFirstLoad ? (
                    <Stack sx={{ p: 2 }} spacing={1.5}>
                        <Skeleton variant="rounded" height={56} />
                        <Skeleton variant="rounded" height={56} />
                        <Skeleton variant="rounded" height={56} />
                    </Stack>
                ) : isError ? (
                    <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            No se pudieron cargar las notificaciones.
                        </Typography>
                        <Button size="small" onClick={() => loadPage(null)}>
                            Reintentar
                        </Button>
                    </Box>
                ) : items.length === 0 ? (
                    <NotificationsEmpty />
                ) : (
                    <List disablePadding>
                        {items.map((n) => (
                            <NotificationItem key={n.id} notification={n} onNavigate={onClose} />
                        ))}
                    </List>
                )}
            </Box>

            {hasMore && !isFirstLoad && !isError && (
                <>
                    <Divider />
                    <Box sx={{ textAlign: "center", py: 1 }}>
                        <Button size="small" onClick={() => loadPage(cursor)} disabled={isFetching}>
                            {isFetching ? "Cargando…" : "Cargar más"}
                        </Button>
                    </Box>
                </>
            )}
        </Popover>
    );
};
